<template>
  <div class="history-plan-selector">
    <a-card title="真实AI调用记录" size="small">
      <a-row :gutter="16">
        <a-col :span="selectedPlanId ? 16 : 24">
          <a-select
            v-model:value="selectedPlanId"
            placeholder="选择最近的调用记录"
            :loading="loading"
            show-search
            :filter-option="false"
            @search="handleSearch"
            @change="handleSelectChange"
            style="width: 100%"
          >
            <template #notFoundContent>
              <div style="padding: 8px; text-align: center;">
                <a-empty :image="simpleImage" description="暂无数据" />
              </div>
            </template>
            <a-select-option
              v-for="option in planOptions"
              :key="option.value"
              :value="option.value"
            >
              <div class="plan-option">
                <div class="plan-main">
                  <a-tag :color="getStatusColor(option.plan.status)">{{ getStatusText(option.plan.status) }}</a-tag>
                  <span class="plan-user-id">{{ option.plan.user_id || '-' }}</span>
                  <span class="plan-task-id">{{ option.plan.task_id }}</span>
                </div>
                <div class="plan-info">
                  <span class="plan-system">{{ option.plan.system_id || '-' }}</span>
                  <span class="plan-fault">{{ option.plan.fault_phenomenon || '-' }}</span>
                  <span class="plan-time">{{ formatTime(option.plan.create_time) }}</span>
                </div>
              </div>
            </a-select-option>
          </a-select>
        </a-col>
        <a-col v-if="selectedPlanId" :span="8">
          <a-button 
            @click="handleRefresh"
            :loading="loading"
            block
          >
            刷新
          </a-button>
        </a-col>
      </a-row>
      
      <!-- 选中计划的详细信息 -->
      <div v-if="selectedPlan" class="selected-plan-info" style="margin-top: 12px">
        <a-descriptions :column="2" size="small" bordered>
          <!-- 第一行：Task ID、User ID -->
          <a-descriptions-item label="Task ID">
            {{ selectedPlan.task_id || '-' }}
          </a-descriptions-item>
          <a-descriptions-item label="User ID">
            {{ selectedPlan.user_id || '-' }}
          </a-descriptions-item>
          <!-- 第二行：方案名称、故障现象 -->
          <a-descriptions-item label="方案名称">
            {{ selectedPlan.scheme_name || '-' }}
          </a-descriptions-item>
          <a-descriptions-item label="故障现象">
            {{ selectedPlan.fault_phenomenon || '-' }}
          </a-descriptions-item>
          <!-- 第三行：SYSNAME、SYSID -->
          <a-descriptions-item label="SYSNAME">
            {{ selectedPlan.system_name || '-' }}
          </a-descriptions-item>
          <a-descriptions-item label="SYSID">
            {{ selectedPlan.system_id || '-' }}
          </a-descriptions-item>
          <!-- 第四行：开始时间、状态 -->
          <a-descriptions-item label="开始时间">
            {{ formatFullTime(selectedPlan.start_time || selectedPlan.create_time) }}
          </a-descriptions-item>
          <a-descriptions-item label="状态">
            <a-tag :color="getStatusColor(selectedPlan.status)">
              {{ getStatusText(selectedPlan.status) }}
            </a-tag>
          </a-descriptions-item>
        </a-descriptions>
      </div>
    </a-card>
  </div>
</template>

<script>
import { ref, watch, onMounted, computed } from 'vue';
import { Empty } from 'ant-design-vue';
import { getHistoryPlans, getHistoryPlanDetail } from '../../api/promptSimulator';
import { message } from 'ant-design-vue';
import dayjs from 'dayjs';

export default {
  name: 'HistoryPlanSelector',
  emits: ['plan-selected', 'plan-cleared'],
  setup(props, { emit }) {
    const selectedPlanId = ref(null);
    const selectedPlan = ref(null);
    const planOptions = ref([]);
    const loading = ref(false);
    const searchKeyword = ref('');
    let searchDebounceTimer = null;

    const simpleImage = Empty.PRESENTED_IMAGE_SIMPLE;

    // 加载历史计划列表
    const loadPlans = async (search = '') => {
      loading.value = true;
      try {
        const result = await getHistoryPlans({
          search: search || undefined,
          limit: 10
        });
        
        if (result.code === 200) {
          planOptions.value = (result.data || []).map(plan => ({
            value: plan.id,
            label: `${plan.task_id} - ${plan.scheme_name}`,
            plan: plan
          }));
        } else {
          message.error(result.message || '加载历史计划失败');
          planOptions.value = [];
        }
      } catch (error) {
        console.error('加载历史计划失败:', error);
        message.error('加载历史计划失败: ' + error.message);
        planOptions.value = [];
      } finally {
        loading.value = false;
      }
    };

    // 搜索处理（防抖）
    const handleSearch = (value) => {
      searchKeyword.value = value;
      if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer);
      }
      searchDebounceTimer = setTimeout(() => {
        loadPlans(value);
      }, 300);
    };

    // 选择变化处理
    const handleSelectChange = async (planId) => {
      if (!planId) {
        selectedPlan.value = null;
        emit('plan-cleared');
        return;
      }

      loading.value = true;
      try {
        const result = await getHistoryPlanDetail(planId);
        if (result.code === 200) {
          selectedPlan.value = result.data.plan;
          emit('plan-selected', result.data);
        } else {
          message.error(result.message || '加载计划详情失败');
          selectedPlanId.value = null;
          selectedPlan.value = null;
        }
      } catch (error) {
        console.error('加载计划详情失败:', error);
        message.error('加载计划详情失败: ' + error.message);
        selectedPlanId.value = null;
        selectedPlan.value = null;
      } finally {
        loading.value = false;
      }
    };

    // 清除选择
    const handleClear = () => {
      selectedPlanId.value = null;
      selectedPlan.value = null;
      searchKeyword.value = '';
      emit('plan-cleared');
      message.success('已清除历史数据');
    };

    // 清空选择（供父组件调用）
    const clearSelection = () => {
      selectedPlanId.value = null;
      selectedPlan.value = null;
      searchKeyword.value = '';
    };

    // 刷新
    const handleRefresh = () => {
      if (selectedPlanId.value) {
        handleSelectChange(selectedPlanId.value);
      } else {
        loadPlans(searchKeyword.value);
      }
    };

    // 格式化时间（简短）
    const formatTime = (time) => {
      if (!time) return '-';
      return dayjs(time).format('MM-DD HH:mm');
    };

    // 格式化时间（完整）
    const formatFullTime = (time) => {
      if (!time) return '-';
      return dayjs(time).format('YYYY-MM-DD HH:mm:ss');
    };

    // 获取状态颜色
    const getStatusColor = (status) => {
      const colorMap = {
        pending: 'default',
        executing: 'processing',
        completed: 'success',
        failed: 'error',
        cancelled: 'warning'
      };
      return colorMap[status] || 'default';
    };

    // 获取状态文本
    const getStatusText = (status) => {
      const textMap = {
        pending: '待执行',
        executing: '执行中',
        completed: '已完成',
        failed: '失败',
        cancelled: '已取消'
      };
      return textMap[status] || status;
    };

    // 初始化
    onMounted(() => {
      loadPlans();
    });

    return {
      selectedPlanId,
      selectedPlan,
      planOptions,
      loading,
      simpleImage,
      handleSearch,
      handleSelectChange,
      handleClear,
      handleRefresh,
      formatTime,
      formatFullTime,
      getStatusColor,
      getStatusText,
      clearSelection
    };
  }
};
</script>

<style scoped>
.history-plan-selector {
  margin-bottom: 16px;
}

.plan-option {
  padding: 0 0 4px 0;
}

.plan-main {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.plan-user-id {
  font-size: 12px;
  color: #666;
  margin-right: 8px;
}

.plan-task-id {
  font-weight: 500;
  font-size: 14px;
}

.plan-info {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #666;
  margin-top: 4px;
}

.plan-system {
  color: #1890ff;
  font-weight: 500;
}

.plan-fault {
  flex: 1;
  color: #666;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.plan-time {
  color: #999;
}

.selected-plan-info {
  background: #fafafa;
  padding: 12px;
  border-radius: 4px;
}
</style>

